/**
 * Bookkeeping for "the application updated".
 *
 * The hard part of an in-place update is not applying it, it is *proving* it
 * happened. A reload looks the same whether it picked up a new bundle or the
 * same one, so the client records the build it is leaving in `sessionStorage`
 * immediately before reloading, and the next bundle compares that record with
 * its own build id. Two different ids is proof the update landed; two equal
 * ids means the reload served the same bytes, which is reported honestly
 * rather than as a success.
 *
 * Everything here is pure or a guarded storage access, so it is unit-tested
 * directly (see `update-flow.spec.ts`).
 */

/** `sessionStorage` key holding the build an accepted update is leaving. */
export const PENDING_UPDATE_KEY = 'arriba.digital.pending-update';

/** What a completed update turned out to be. */
export type UpdateOutcome =
  | { readonly kind: 'updated'; readonly from: string; readonly to: string }
  | { readonly kind: 'current'; readonly build: string };

/**
 * A storage handle, or `null` when the browser refuses one.
 *
 * `sessionStorage` throws on access in some privacy modes rather than being
 * absent, so the read itself is inside the `try`. Losing this record is not
 * fatal — it only costs the post-update confirmation.
 */
function defaultStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** Record that an update starting from `from` is about to be applied. */
export function beginPendingUpdate(from: string, store: Storage | null = defaultStorage()): void {
  try {
    store?.setItem(PENDING_UPDATE_KEY, from);
  } catch {
    // A full or blocked store must never stop the reload itself.
  }
}

/** The build an update was started from, or `null` if no update is pending. */
export function readPendingUpdate(store: Storage | null = defaultStorage()): string | null {
  try {
    return store?.getItem(PENDING_UPDATE_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Forget the pending update; called once its outcome has been reported. */
export function clearPendingUpdate(store: Storage | null = defaultStorage()): void {
  try {
    store?.removeItem(PENDING_UPDATE_KEY);
  } catch {
    // Nothing to do: the record is only a hint for the next load.
  }
}

/**
 * Decide what a reload achieved.
 *
 * @param from - The build recorded before reloading, or `null` when the load
 *   was not part of an update at all (the ordinary case).
 * @param running - The build id of the bundle that is running now.
 * @returns The outcome to report, or `null` when there is nothing to say.
 */
export function resolveUpdateOutcome(from: string | null, running: string): UpdateOutcome | null {
  if (from === null) return null;
  return from === running
    ? { kind: 'current', build: running }
    : { kind: 'updated', from, to: running };
}
